import renderHtml from 'htmr';

/** Gatsby/GraphQL may return Prismic StructuredText raw as an array or JSON string. */
export const coercePrismicRichTextRaw = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const mapSpansToSegment = (spans, segStart, trimmed) => {
  const segEnd = segStart + trimmed.length;
  return (spans || [])
    .filter((span) => span.end > segStart && span.start < segEnd)
    .map((span) => ({
      ...span,
      start: Math.max(0, span.start - segStart),
      end: Math.min(trimmed.length, span.end - segStart),
    }));
};

/** Soft line breaks inside one Prismic paragraph → separate blocks (spacing without extra Enter). */
const splitParagraphBlockOnLineBreaks = (block) => {
  if (!block || block.type !== 'paragraph') return [block];

  const text = String(block.text ?? '');
  if (!/[\r\n]/.test(text)) return [block];

  const segments = [];
  let partStart = 0;

  const pushSegment = (start, end) => {
    const slice = text.slice(start, end);
    const trimmed = slice.trim();
    if (!trimmed) return;
    const trimLead = slice.length - slice.trimStart().length;
    const segStart = start + trimLead;
    segments.push({
      ...block,
      text: trimmed,
      spans: mapSpansToSegment(block.spans, segStart, trimmed),
    });
  };

  for (let i = 0; i < text.length; i += 1) {
    if (text[i] === '\r' && text[i + 1] === '\n') {
      pushSegment(partStart, i);
      i += 1;
      partStart = i + 1;
    } else if (text[i] === '\n' || text[i] === '\r') {
      pushSegment(partStart, i);
      partStart = i + 1;
    }
  }
  pushSegment(partStart, text.length);

  return segments.length > 1 ? segments : [block];
};

export const expandGcRichTextRaw = (raw) =>
  coercePrismicRichTextRaw(raw).flatMap(splitParagraphBlockOnLineBreaks);

export const prepareGcRichTextRaw = (raw) => expandGcRichTextRaw(raw);

const splitInnerHtmlIntoParagraphs = (inner, attrStr = '') => {
  if (!/[\r\n]/.test(inner) && !/<br\s*\/?>/i.test(inner)) return null;

  const parts = /<br\s*\/?>/i.test(inner)
    ? inner.split(/<br\s*\/?>/gi)
    : inner.split(/\r?\n+/);

  const trimmed = parts.map((part) => part.trim()).filter(Boolean);
  if (trimmed.length <= 1) return null;

  return trimmed.map((part) => `<p class="gc-read-paragraph">${part}</p>`).join('');
};

/** Normalize Prismic HTML for paragraph spacing (study chapters use html-only). */
export const normalizeGcChapterBodyHtml = (html = '') => {
  let s = String(html || '').trim();
  if (!s) return s;

  if (!/<p[\s>]/i.test(s)) {
    const parts = s
      .split(/\r?\n+/)
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length > 1) {
      return parts.map((part) => `<p class="gc-read-paragraph">${part}</p>`).join('');
    }
  }

  s = s.replace(/(<br\s*\/?>\s*){2,}/gi, '</p><p class="gc-read-paragraph">');
  s = s.replace(/<p\b(?![^>]*\bclass=)([^>]*)>/gi, '<p class="gc-read-paragraph"$1>');

  s = s.replace(/<p(\s[^>]*)?>([\s\S]*?)<\/p>/gi, (match, attrs, inner) => {
    const split = splitInnerHtmlIntoParagraphs(inner, attrs || '');
    return split || match;
  });

  return s;
};

export const renderGcChapterHtmlBlocks = (html, { transform } = {}) => {
  const normalized = normalizeGcChapterBodyHtml(html);
  if (!normalized) return null;
  return renderHtml(normalized, transform ? { transform } : undefined);
};

const isMostlyAllCaps = (plain) => {
  const letters = String(plain || '').replace(/[^A-Za-z]/g, '');
  if (letters.length < 4) return false;
  const upper = (letters.match(/[A-Z]/g) || []).length;
  return upper / letters.length >= 0.75;
};

const sentenceCaseFromAllCaps = (text) => {
  const lower = String(text).toLowerCase();
  return lower.replace(/(^|[.!?]\s+)([a-z])/g, (_, prefix, letter) => prefix + letter.toUpperCase());
};

/** Quote slices stored in ALL CAPS in Prismic → normal sentence case for a uniform read experience. */
export const normalizeGcQuoteText = (input = '') => {
  let s = String(input ?? '').trim();
  if (!s) return s;

  const plain = s.replace(/<[^>]+>/g, '');
  if (!isMostlyAllCaps(plain)) return s;

  if (!/<[a-z]/i.test(s)) {
    return sentenceCaseFromAllCaps(s);
  }

  return s.replace(/>([^<]+)</g, (match, text) => {
    if (!text.trim() || !isMostlyAllCaps(text)) return match;
    return `>${sentenceCaseFromAllCaps(text)}<`;
  });
};
